import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import clsx from "clsx";
import SlideOver from "../../Modal/SlideOver";
import Button from "../../Button";
import FormSelect from "../../Form/FormSelect";
import FormDate from "../../Form/FormDate";
import FormInput from "../../Form/FormInput";
import FormLabel from "../../Form/FormLabel";
import ItemTaxPicker from "../ItemTaxPicker";
import TaxBaseField from "../TaxBaseField";
import PeriodClosedNotice from "../PeriodClosedNotice";
import {
  createDeposit,
  requestListAccount,
  requestListBusinessPartner,
  requestListBusinessTax,
  updateDeposit,
  useAccountingDispatch,
  useAccountingState,
  useBusinessTaxDispatch,
  useBusinessTaxState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  usePeriodLock,
} from "../../../../Context";
import { TAX_DIRECTORY_LIMIT } from "../../../../Constants/taxCategories";
import { TAX_BASE_TYPES } from "../../../../Constants/products";
import { formatCurrency } from "../../../../Utils";
import { getCashBankOptions } from "../../../../Utils/AccountingUtil";
import { getApiErrorBody } from "../../../../Utils/StringUtils";
import {
  DEPOSIT_OTHER_TAX_BASE,
  calculateDepositTotals,
  getDepositActionError,
  isWholeFraction,
} from "../../../../Utils/DepositUtil";
import FormError from "../../Form/FormError";

// The API's 400 for DPP Nilai Lain, keyed on the numerator; shown under the
// fraction instead of in the footer.
const getFractionError = (error) => {
  const message = getApiErrorBody(error)?.error?.tax_base_numerator;
  return Array.isArray(message) ? message.join(" ") : message || null;
};

// A deposit's DPP Nilai Lain is always a fraction of the amount.
const DEPOSIT_TAX_BASE_TYPES = TAX_BASE_TYPES.filter(
  (type) => type.value === "FRACTION",
);
const FRACTION_TYPE = DEPOSIT_TAX_BASE_TYPES[0];

// The API takes whole numbers 1-1000 with the numerator no larger.
const parseFraction = (numerator, denominator) => {
  const num = Number(numerator);
  const den = Number(denominator);
  const valid =
    Number.isInteger(num) &&
    Number.isInteger(den) &&
    num >= 1 &&
    den <= 1000 &&
    num <= den;
  return valid ? { numerator: num, denominator: den } : null;
};

/**
 * W2 · Tambah / Ubah Uang Muka.
 *
 * `opening` is the per-customer opening balance mode used from Saldo Awal: no
 * cash account and no tax (the money arrived before the books start), and the
 * date is the opening balance date, fixed.
 *
 * `onSaved(result)` gets the API's `{ id, depositNumber }`.
 */
const DepositModal = ({
  open,
  onClose,
  onSaved,
  businessID,
  deposit,
  partner: presetPartner,
  opening = false,
  openingDate,
}) => {
  const { t, i18n } = useTranslation();
  const isEdit = Boolean(deposit?.id);
  const isOpening = opening || Boolean(deposit?.opening);

  const accountingDispatch = useAccountingDispatch();
  const partnerDispatch = useBusinessTransactionDispatch();
  const taxDispatch = useBusinessTaxDispatch();
  const { accounts, accountLoading, allAccountLoading } = useAccountingState();
  const { businessPartners, businessPartnerLoading, businessPartnerError } =
    useBusinessTransactionState();
  const { businessTaxes, businessTaxesLoading, businessTaxesError } =
    useBusinessTaxState();

  const {
    minDate,
    isDateClosed,
    isClosed,
    closedThroughLabel,
    refetch: refetchPeriod,
  } = usePeriodLock(businessID, { locale: i18n.language });

  const [partner, setPartner] = useState(null);
  const [date, setDate] = useState(new Date());
  const [account, setAccount] = useState(null);
  const [taxBase, setTaxBase] = useState("");
  const [taxes, setTaxes] = useState([]);
  const [taxBaseType, setTaxBaseType] = useState(null);
  const [taxBaseNumerator, setTaxBaseNumerator] = useState("");
  const [taxBaseDenominator, setTaxBaseDenominator] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!open || !businessID) return;
    requestListBusinessPartner(partnerDispatch, { businessID });
    if (!isOpening) {
      requestListAccount(accountingDispatch, { businessID, loadAll: true });
      requestListBusinessTax(taxDispatch, {
        page: 1,
        limit: TAX_DIRECTORY_LIMIT,
        businessID,
      });
    }
  }, [
    open,
    businessID,
    isOpening,
    partnerDispatch,
    accountingDispatch,
    taxDispatch,
  ]);

  const partnerOptions = useMemo(
    () =>
      (businessPartners || []).map((item) => ({
        ...item,
        value: item.id,
        label: item.name,
      })),
    [businessPartners],
  );

  const accountOptions = useMemo(
    () => getCashBankOptions(accounts),
    [accounts],
  );

  // Sales taxes only, the same set the invoice line offers.
  const salesTaxes = useMemo(
    () => (businessTaxes || []).filter((tax) => tax.type !== "PURCHASES"),
    [businessTaxes],
  );

  // Filled once per opening; later list refreshes only supply labels.
  const filled = useRef(false);
  useEffect(() => {
    if (!open) {
      filled.current = false;
      return;
    }
    if (filled.current) return;
    filled.current = true;
    setErrors({});
    setSubmitError(null);
    if (isEdit) {
      setPartner(
        deposit.partner
          ? {
              ...deposit.partner,
              value: deposit.partner.id,
              label: deposit.partner.name,
            }
          : null,
      );
      setDate(deposit.createdAt ? new Date(deposit.createdAt) : new Date());
      setAccount(
        deposit.account
          ? { value: deposit.account.id, label: deposit.account.name }
          : null,
      );
      setTaxBase(deposit.taxBase ?? "");
      setTaxes(
        (deposit.taxes || []).map((tax) => ({ id: tax.id, name: tax.name })),
      );
      const saved = deposit.taxBaseFraction;
      const hasFraction = !isWholeFraction(saved);
      setTaxBaseType(hasFraction ? FRACTION_TYPE : null);
      setTaxBaseNumerator(hasFraction ? String(saved.numerator) : "");
      setTaxBaseDenominator(hasFraction ? String(saved.denominator) : "");
      setDescription(deposit.description || "");
    } else {
      setPartner(
        presetPartner
          ? {
              ...presetPartner,
              value: presetPartner.id,
              label: presetPartner.name,
            }
          : null,
      );
      setDate(isOpening && openingDate ? new Date(openingDate) : new Date());
      setAccount(null);
      setTaxBase("");
      setTaxes([]);
      setTaxBaseType(null);
      setTaxBaseNumerator("");
      setTaxBaseDenominator("");
      setDescription("");
    }
  }, [open, isEdit, deposit, presetPartner, isOpening, openingDate]);

  // A tax chip only carries id and name until the business's list arrives;
  // the rate and the withholding flag come from there.
  const resolvedTaxes = useMemo(() => {
    const byID = new Map(salesTaxes.map((tax) => [tax.id, tax]));
    return taxes.map((tax) => byID.get(tax.id ?? tax.value) || tax);
  }, [taxes, salesTaxes]);

  // No tax means no DPP Nilai Lain (an opening balance deposit never has one).
  const fraction =
    !isOpening && taxes.length > 0 && taxBaseType?.value === "FRACTION"
      ? parseFraction(taxBaseNumerator, taxBaseDenominator)
      : null;

  const totals = useMemo(
    () =>
      calculateDepositTotals(taxBase, isOpening ? [] : resolvedTaxes, fraction),
    [taxBase, resolvedTaxes, isOpening, fraction],
  );

  const fractionError = getFractionError(submitError);

  const dateLocked = !isOpening && isDateClosed(date);

  const validate = () => {
    const next = {};
    if (!partner?.value) next.partner = "deposits.errors.partner";
    if (!date) next.date = "deposits.errors.date";
    if (!isOpening && !account?.value) next.account = "deposits.errors.account";
    if (!(Number(taxBase) > 0)) next.taxBase = "deposits.errors.amount";
    if (!isOpening && taxes.length > 0 && taxBaseType?.value === "FRACTION") {
      if (taxBaseNumerator === "")
        next.taxBaseNumerator = "products.errors.taxBaseNumeratorRequired";
      if (taxBaseDenominator === "")
        next.taxBaseDenominator = "products.errors.taxBaseDenominatorRequired";
      if (!next.taxBaseNumerator && !next.taxBaseDenominator && !fraction)
        next.taxBaseFraction = "deposits.errors.taxBaseFraction";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (submitting) return;
    if (!validate() || dateLocked) return;

    const body = {
      business_id: businessID,
      partner_id: partner.value,
      created_at: (isOpening && openingDate
        ? new Date(openingDate)
        : date
      ).toISOString(),
      amount: Math.round(Number(taxBase)),
      tax_ids: isOpening ? [] : taxes.map((tax) => tax.id ?? tax.value),
      ...(fraction && {
        tax_base_numerator: fraction.numerator,
        tax_base_denominator: fraction.denominator,
      }),
      ...(!isOpening && { account_id: account.value }),
      ...(description.trim() && { description: description.trim() }),
      ...(isOpening && { opening: true }),
    };

    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = isEdit
        ? await updateDeposit({ id: deposit.id, ...body })
        : await createDeposit(body);
      setSubmitting(false);
      onSaved?.(result);
    } catch (error) {
      setSubmitting(false);
      setSubmitError(error);
    }
  };

  const clear = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const clearFraction = () => {
    setErrors((prev) => ({
      ...prev,
      taxBaseNumerator: undefined,
      taxBaseDenominator: undefined,
      taxBaseFraction: undefined,
    }));
    setSubmitError(null);
  };

  const title = isEdit
    ? "deposits.modal.titleEdit"
    : isOpening
      ? "deposits.modal.titleOpening"
      : "deposits.modal.titleAdd";

  const dirty =
    !isEdit && Boolean(partner || taxBase || taxes.length || account);

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      dirty={dirty}
      title={title}
      footer={
        <div className="flex flex-col gap-2">
          {!fractionError && (
            <FormError error={getDepositActionError(submitError, t)} />
          )}
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-gray-500">
                {t("deposits.fields.total")}
              </p>
              <p
                className="text-sm font-semibold tabular-nums text-gray-900"
                data-testid="deposit-total"
              >
                {formatCurrency(totals.total)}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                form="deposit-form"
                loading={submitting}
                disabled={dateLocked}
              >
                {isEdit ? t("common.save") : t("common.create")}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <form
        id="deposit-form"
        className="mb-6 space-y-4"
        onSubmit={handleSubmit}
      >
        <p className="rounded-md bg-blue-50 p-3 text-sm text-blue-800">
          {t(
            isOpening ? "deposits.modal.openingIntro" : "deposits.modal.intro",
          )}
        </p>

        <PeriodClosedNotice error={submitError} onRefresh={refetchPeriod} />

        <FormSelect
          required
          id="deposit-partner"
          label="deposits.fields.customer"
          searchable
          options={partnerOptions}
          isLoading={businessPartnerLoading}
          value={partner}
          disabled={isEdit && deposit?.usages?.length > 0}
          onChange={(value) => {
            setPartner(value);
            clear("partner");
          }}
          placeholder={t("deposits.placeholders.customer")}
          error={businessPartnerError || errors.partner}
        />

        {isOpening ? (
          <div>
            <FormLabel>{t("deposits.fields.date")}</FormLabel>
            <p className="mt-1 text-sm text-gray-900">
              {openingDate || deposit?.createdAt
                ? dayjs(openingDate || deposit.createdAt)
                    .locale(i18n.language)
                    .format("DD MMM YYYY")
                : "-"}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {t("deposits.modal.openingDate")}
            </p>
          </div>
        ) : (
          <div>
            <FormDate
              required
              label="deposits.fields.date"
              selected={date}
              onChange={(value) => {
                setDate(value);
                clear("date");
              }}
              dateFormat="dd/MM/yyyy HH:mm"
              showTimeInput
              timeInputLabel={t("common.time")}
              minDate={minDate}
              isDateClosed={isDateClosed}
              error={errors.date}
            />
            {isClosed && (
              <p className="mt-1.5 text-xs text-amber-700">
                {dateLocked
                  ? t("accounting.period.hints.dateLocked", {
                      date: closedThroughLabel,
                    })
                  : t("accounting.period.banner.closedThrough", {
                      date: closedThroughLabel,
                    })}
              </p>
            )}
          </div>
        )}

        {!isOpening && (
          <FormSelect
            required
            id="deposit-account"
            label="deposits.fields.account"
            searchable
            options={accountOptions}
            isLoading={accountLoading || allAccountLoading}
            value={account}
            onChange={(value) => {
              setAccount(value);
              clear("account");
            }}
            placeholder={t("deposits.placeholders.account")}
            error={errors.account}
          />
        )}

        <FormInput
          required
          id="deposit-tax-base"
          label="deposits.fields.taxBase"
          value={taxBase}
          numberFormat={{
            thousandSeparator: ".",
            decimalSeparator: ",",
            decimalScale: 0,
            allowNegative: false,
            valueIsNumericString: true,
            onValueChange: ({ value }) => {
              setTaxBase(value);
              clear("taxBase");
            },
          }}
          prefix="Rp"
          inputMode="numeric"
          error={errors.taxBase}
        />

        {!isOpening && (
          <div>
            <FormLabel htmlFor="deposit-taxes">
              {t("deposits.fields.taxes")}
            </FormLabel>
            <ItemTaxPicker
              id="deposit-taxes"
              options={salesTaxes}
              value={taxes}
              onChange={(next) => {
                setTaxes(next);
                // As on an invoice line: DPP Nilai Lain belongs to the taxes,
                // so emptying the list drops it.
                if (!next.length) {
                  setTaxBaseType(null);
                  setTaxBaseNumerator("");
                  setTaxBaseDenominator("");
                  clearFraction();
                }
              }}
              isLoading={businessTaxesLoading}
              error={businessTaxesError}
              onRefresh={() =>
                requestListBusinessTax(taxDispatch, {
                  page: 1,
                  limit: TAX_DIRECTORY_LIMIT,
                  businessID,
                })
              }
            />
          </div>
        )}

        {!isOpening && taxes.length > 0 && (
          <TaxBaseField
            id="deposit-tax-base-type"
            label="deposits.modal.otherTaxBase"
            description="deposits.modal.otherTaxBaseHint"
            types={DEPOSIT_TAX_BASE_TYPES}
            type={taxBaseType}
            onTypeChange={(value) => {
              setTaxBaseType(value);
              // 11/12 is the usual DPP Nilai Lain for PPN 12%.
              if (value && taxBaseNumerator === "" && taxBaseDenominator === "") {
                setTaxBaseNumerator(String(DEPOSIT_OTHER_TAX_BASE.numerator));
                setTaxBaseDenominator(
                  String(DEPOSIT_OTHER_TAX_BASE.denominator),
                );
              }
              clearFraction();
            }}
            numerator={taxBaseNumerator}
            onNumeratorChange={(value) => {
              setTaxBaseNumerator(value);
              clearFraction();
            }}
            denominator={taxBaseDenominator}
            onDenominatorChange={(value) => {
              setTaxBaseDenominator(value);
              clearFraction();
            }}
            result={fraction ? String(totals.otherTaxBase) : ""}
            numberFormat={{
              thousandSeparator: ".",
              decimalSeparator: ",",
              decimalScale: 0,
            }}
            errors={errors}
            error={errors.taxBaseFraction || fractionError}
          />
        )}

        <FormInput
          id="deposit-description"
          label="deposits.fields.description"
          value={description}
          placeholder={t("deposits.placeholders.description")}
          onChange={(e) => setDescription(e.target.value)}
          onClear={() => setDescription("")}
          hint={t("common.optional")}
        />

        <dl className="space-y-1 border-t border-gray-200 pt-3 text-sm">
          <div className="flex justify-between">
            <dt>{t("invoices.fields.taxBase")}</dt>
            <dd className="tabular-nums">{formatCurrency(totals.taxBase)}</dd>
          </div>
          {fraction && (
            <div className="flex justify-between">
              <dt>
                {t("deposits.fields.otherTaxBase", {
                  interpolation: { escapeValue: false },
                  fraction: `${fraction.numerator}/${fraction.denominator}`,
                })}
              </dt>
              <dd className="tabular-nums" data-testid="deposit-other-tax-base-amount">
                {formatCurrency(totals.otherTaxBase)}
              </dd>
            </div>
          )}
          {totals.taxes.map((tax) => (
            <div
              key={tax.id}
              className={clsx(
                "flex justify-between",
                tax.withholding && "text-amber-700",
              )}
            >
              <dt>
                {tax.withholding
                  ? t("deposits.withheld", {
                      interpolation: { escapeValue: false },
                      name: tax.name || t("taxes.unavailable"),
                    })
                  : tax.name || t("taxes.unavailable")}
              </dt>
              <dd className="tabular-nums">
                {tax.withholding ? "−" : ""}
                {formatCurrency(tax.amount)}
              </dd>
            </div>
          ))}
          <div className="flex justify-between border-t border-gray-200 pt-2 font-semibold">
            <dt>{t("deposits.fields.total")}</dt>
            <dd className="tabular-nums">{formatCurrency(totals.total)}</dd>
          </div>
        </dl>
      </form>
    </SlideOver>
  );
};

export default DepositModal;
